import { Fragment, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { LayoutPresetId, MediaItem } from "@gooner/shared";
import { clipKey, LAYOUT_PRESETS } from "@gooner/shared";
import { ClipCard } from "../../components/ClipCard";
import { PresetPalette } from "../components/PresetPalette";
import { GoldDialog } from "../components/GoldDialog";
import { type GoldGate, trackGoldGate, useGoldGate } from "../../lib/gold";
import { DEFAULT_PLAY_LAYOUT } from "../../lib/playCollection";
import { Button, cx } from "../ui/primitives";
import { IconBack, IconCheck, IconLayout, IconPlay } from "../ui/icons";
import type { Selection } from "./useSelection";
import { MASK } from "../../lib/replayMask";

/** Desktop panel width (`w-72`). On a phone-narrow viewport we expand toward the
 *  margins instead so the 3-col PresetPalette is not clipped. */
const LAYOUT_PANEL_W = 288;
const LAYOUT_PANEL_MARGIN = 8;

/** The full, Gold-gated layout picker used beside every collection's Play button —
 *  the same `LAYOUT_PRESETS` the view editor offers, in the same `PresetPalette` grid,
 *  so the two never drift. A gold layout (region count past the tier ceiling) shows the
 *  GoldChip and, for a non-Gold viewer, routes the click to the upsell instead of
 *  selecting.
 *
 *  The panel is portalled and viewport-clamped (same idea as AddToCollection): an
 *  `absolute left-0 w-72` under the Grid button overflows a 390px phone — third column
 *  cut off, labels truncated, page-level horizontal scroll. */
function PlayLayoutPicker({
  layout,
  onLayout,
}: {
  layout: LayoutPresetId;
  onLayout: (id: LayoutPresetId) => void;
}) {
  const [open, setOpen] = useState(false);
  const [goldGate, setGoldGate] = useState<GoldGate | null>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const { maxRegions } = useGoldGate();
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const current = LAYOUT_PRESETS.find((p) => p.id === layout);

  function placePanel() {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const available = window.innerWidth - LAYOUT_PANEL_MARGIN * 2;
    // Phone-narrow: use the full remaining width so 3-col cells/labels are not cramped.
    // Desktop: keep the familiar w-72.
    const width = available < 420 ? available : Math.min(LAYOUT_PANEL_W, available);
    // Prefer the trigger's left edge; shift left when that would spill past the right margin.
    const left = Math.min(
      Math.max(LAYOUT_PANEL_MARGIN, r.left),
      window.innerWidth - width - LAYOUT_PANEL_MARGIN,
    );
    setPanelStyle({ top: r.bottom + 6, left, width });
  }

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    placePanel();
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) =>
      !!(t instanceof Node && (ref.current?.contains(t) || panelRef.current?.contains(t)));
    const onDown = (e: MouseEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    // Placed from the button's rect — scroll/resize closes rather than leaving it adrift.
    const onReposition = (e: Event) => {
      if (e.type === "scroll" && inside(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button variant="subtle" onClick={toggle}>
        <IconLayout width={15} height={15} /> {current?.label ?? "Layout"}
      </Button>
      {open && panelStyle
        ? createPortal(
            <div
              ref={panelRef}
              style={panelStyle}
              className="fixed z-30 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-2 shadow-xl"
            >
              <PresetPalette
                onApply={(id) => {
                  onLayout(id);
                  setOpen(false);
                }}
                aspect={{ w: 16, h: 9 }}
                maxRegions={maxRegions}
                onLocked={() => {
                  trackGoldGate("layout");
                  setGoldGate("layout");
                }}
                pageSize={9}
                cols={3}
              />
            </div>,
            document.body,
          )
        : null}
      {goldGate ? <GoldDialog source={goldGate} onClose={() => setGoldGate(null)} /> : null}
    </div>
  );
}

/** Tiny "3 days ago" / "on Jul 5, 2026" formatter — good enough, no dependency. */
export function relativeDate(ts: number): string {
  const diff = Date.now() - ts;
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < hour) {
    const m = Math.max(1, Math.floor(diff / min));
    return m === 1 ? "1 minute ago" : `${m} minutes ago`;
  }
  if (diff < day) {
    const h = Math.floor(diff / hour);
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }
  if (diff < 30 * day) {
    const d = Math.floor(diff / day);
    return d === 1 ? "1 day ago" : `${d} days ago`;
  }
  return (
    "on " +
    new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  );
}

/**
 * The single collection presentation page — one header (title, meta line, description),
 * a Play control (layout picker + a throwaway grid display), and the clip grid. Shared
 * by the public Browse detail (Editor's Choice / community) AND a user's own collection,
 * so both look identical; only the injected slots differ:
 *
 *   - `headerActions` — public: "Add to my collections"; owner: "Delete".
 *   - `renderTileActions` — owner: the thumbnail star + remove. Every card already opens
 *     its popup on a click and has the crown and add-to-collection (ClipCard), public or not.
 *   - `onRename` — owner only: makes the title an inline-editable field.
 *   - `ownerBar` — owner only: public/private toggle + description editor.
 *   - `onPlay` — every collection has it now, Faps included: the caller plays Faps under
 *     the owner-scoped `faps:<uid>` id, which the display source resolves per-user.
 *   - `selection` + `selectionBar` — owner only (issue #12): bulk select. A Select
 *     button beside Play, selectable cards, and the caller's action bar while it is on.
 *
 * Root is container-less (`space-y-6`); the caller supplies width/padding.
 */
export function CollectionView({
  backTo,
  title,
  onRename,
  meta,
  description,
  ownerBar,
  items,
  gridBreak,
  onPlay,
  headerActions,
  thumbnailClipId,
  renderTileActions,
  selection,
  selectionBar,
}: {
  backTo: { to: string; label: string };
  title: string;
  onRename?: (name: string) => void;
  meta: ReactNode;
  description?: string;
  ownerBar?: ReactNode;
  items: MediaItem[];
  /** A full-width row placed after the 15th clip, only on a collection longer than that. The
   *  public collection page passes its ad here; the owner's editor passes nothing. */
  gridBreak?: ReactNode;
  onPlay?: (layout: LayoutPresetId) => void;
  headerActions?: ReactNode;
  thumbnailClipId?: string | null;
  renderTileActions?: (clip: MediaItem) => ReactNode;
  selection?: Selection;
  selectionBar?: ReactNode;
}) {
  // The shared free-safe default, never a literal. This was "grid-3x3" — nine regions, one
  // over Basic's eight — so on 2026-09-25 a Basic or signed-out viewer pressing Play on any
  // collection page got the Premium upsell (a 409 on the mint) while picking 2×2 by hand
  // worked. Choosing a Gold layout in the picker is still allowed; that is a deliberate ask,
  // and PresetPalette routes it to the upsell.
  const [layout, setLayout] = useState<LayoutPresetId>(DEFAULT_PLAY_LAYOUT);
  const [name, setName] = useState(title);

  return (
    <div className="space-y-6">
      <Link
        to={backTo.to}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
      >
        <IconBack width={14} height={14} /> {backTo.label}
      </Link>

      <header className="space-y-2">
        {onRename ? (
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const n = name.trim();
              if (n && n !== title) onRename(n);
              else setName(title);
            }}
            aria-label="Collection name"
            className="w-full max-w-2xl border-0 border-b border-transparent bg-transparent px-0 text-2xl font-semibold text-zinc-100 focus:border-[var(--color-accent)]/60 focus:outline-none"
          />
        ) : (
          <h1 className="text-2xl font-semibold text-zinc-100" {...MASK}>{title}</h1>
        )}

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-zinc-400">{meta}</div>

        {description ? <p className="max-w-2xl text-sm text-zinc-400" {...MASK}>{description}</p> : null}
      </header>

      {ownerBar}

      {onPlay || headerActions || selection ? (
        <div className="flex flex-wrap items-center gap-3">
          {onPlay ? (
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={() => onPlay(layout)}>
                <IconPlay width={15} height={15} /> Play
              </Button>
              <PlayLayoutPicker layout={layout} onLayout={setLayout} />
            </div>
          ) : null}
          {headerActions}
          {selection && items.length > 0 ? (
            selection.active ? (
              <span className="text-sm text-zinc-400">
                Selecting<span className="hidden pointer-fine:inline"> · Esc to exit</span>
              </span>
            ) : (
              <Button variant="ghost" onClick={() => selection.enter("button")}>
                <IconCheck />
                Select
              </Button>
            )
          ) : null}
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-500">This collection has no clips yet.</p>
      ) : (
        // While the action bar shows, room under the last row so the bar never covers it.
        <div
          className={cx(
            "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5",
            selection?.active && "pb-28",
          )}
        >
          {items.map((clip, i) => {
            const isThumbnail = thumbnailClipId != null && thumbnailClipId === clipKey(clip);
            return (
              <Fragment key={clipKey(clip)}>
              <ClipCard
                clip={clip}
                list={items}
                surface="collection"
                badge={
                  isThumbnail ? (
                    <span className="rounded bg-[var(--color-accent)] px-1.5 py-0.5 text-[10px] font-medium text-white">
                      Thumbnail
                    </span>
                  ) : undefined
                }
                extraActions={renderTileActions?.(clip)}
                select={
                  selection
                    ? {
                        active: selection.active,
                        selected: selection.selected.has(clipKey(clip)),
                        onToggle: (range) => selection.toggle(clipKey(clip), range),
                        onEnter: (entry) => selection.enter(entry, clipKey(clip)),
                      }
                    : undefined
                }
              />
              {i === 14 && items.length > 15 && gridBreak ? (
                <div className="col-span-full flex justify-center py-2">{gridBreak}</div>
              ) : null}
              </Fragment>
            );
          })}
        </div>
      )}
      {selection?.active ? selectionBar : null}
    </div>
  );
}
